import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import BoardAvatar from "./board-avatar";
import "./filter-chips.scss";
import { DirectoryMember, displayNameOf } from "./people-directory";
import { IssuePriority, IssueType, ISSUE_TYPE_LABELS, PRIORITY_COLOR, PRIORITY_LABELS, PRIORITY_ORDER } from "./types";

export interface BoardFilters {
  // Stable user id of the assignee filter (null = no assignee filter).
  assignee: string | null;
  priority: IssuePriority | null;
  type: IssueType | null;
}

type GroupByValue = "none" | "assignee" | "priority" | "epic";

interface FilterChipsProps {
  filters: BoardFilters;
  assigneeOptions: DirectoryMember[];
  groupBy: GroupByValue;
  onGroupByChange: (next: GroupByValue) => void;
  onChange: (next: BoardFilters) => void;
  onClear: () => void;
}

interface ChipMenuProps<TValue> {
  label: string;
  value: TValue | null;
  options: { value: TValue; label: string }[];
  formatValue?: (value: TValue) => React.ReactNode;
  emptyLabel?: string;
  onSelect: (next: TValue | null) => void;
}

function ChipMenu<TValue extends string>({
  label,
  value,
  options,
  formatValue,
  emptyLabel = "Any",
  onSelect
}: ChipMenuProps<TValue>): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent): void => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  let display: React.ReactNode = emptyLabel;
  if (value) {
    display = formatValue
      ? formatValue(value)
      : (options.find((entry) => entry.value === value)?.label ?? String(value));
  }

  return (
    <div ref={wrapperRef} className={classNames("kuika-board-chip", { "kuika-board-chip--set": value !== null })}>
      <button type="button" className="kuika-board-chip__button" onClick={() => setOpen((p) => !p)}>
        <span className="kuika-board-chip__label">{label}</span>
        <span className="kuika-board-chip__value">{display}</span>
        <span className="kuika-board-chip__caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div className="kuika-board-chip__menu" role="menu">
          <button
            type="button"
            className={classNames("kuika-board-chip__option", {
              "kuika-board-chip__option--active": value === null
            })}
            onClick={() => {
              onSelect(null);
              setOpen(false);
            }}
          >
            {emptyLabel}
          </button>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={classNames("kuika-board-chip__option", {
                "kuika-board-chip__option--active": option.value === value
              })}
              onClick={() => {
                onSelect(option.value);
                setOpen(false);
              }}
            >
              {formatValue ? formatValue(option.value) : option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const GROUP_BY_OPTIONS: { value: GroupByValue; label: string }[] = [
  { value: "assignee", label: "Assignee" },
  { value: "priority", label: "Priority" },
  { value: "epic", label: "Epic" }
];

const FilterChips: React.FC<FilterChipsProps> = ({
  filters,
  assigneeOptions,
  groupBy,
  onGroupByChange,
  onChange,
  onClear
}) => {
  const anyActive: boolean = filters.assignee !== null || filters.priority !== null || filters.type !== null;
  return (
    <div className="kuika-board-chips">
      <ChipMenu<GroupByValue>
        label="Group"
        value={groupBy === "none" ? null : groupBy}
        emptyLabel="None"
        options={GROUP_BY_OPTIONS}
        onSelect={(next: GroupByValue | null) => onGroupByChange(next ?? "none")}
      />
      <span className="kuika-board-chips__divider" aria-hidden="true" />
      <ChipMenu
        label="Assignee"
        value={filters.assignee}
        options={assigneeOptions.map((member: DirectoryMember) => ({ value: member.id, label: member.name }))}
        formatValue={(value: string) => (
          <span className="kuika-board-chip__assignee">
            <BoardAvatar userId={value} className="kuika-board__assignee-avatar" ariaHidden />
            {displayNameOf(value)}
          </span>
        )}
        onSelect={(next: string | null) => onChange({ ...filters, assignee: next })}
      />
      <ChipMenu<IssuePriority>
        label="Priority"
        value={filters.priority}
        options={PRIORITY_ORDER.map((p: IssuePriority) => ({ value: p, label: PRIORITY_LABELS[p] }))}
        formatValue={(value: IssuePriority) => (
          <span className="kuika-board-chip__priority">
            <span className="kuika-board-chip__priority-dot" style={{ background: PRIORITY_COLOR[value] }} />
            {PRIORITY_LABELS[value]}
          </span>
        )}
        onSelect={(next: IssuePriority | null) => onChange({ ...filters, priority: next })}
      />
      <ChipMenu<IssueType>
        label="Type"
        value={filters.type}
        options={(["task", "feature", "bug", "chore", "epic"] as IssueType[]).map((t: IssueType) => ({
          value: t,
          label: ISSUE_TYPE_LABELS[t]
        }))}
        onSelect={(next: IssueType | null) => onChange({ ...filters, type: next })}
      />
      {anyActive && (
        <button type="button" className="kuika-board-chips__clear" onClick={onClear}>
          Clear all
        </button>
      )}
      <AssigneeStack
        members={assigneeOptions}
        selected={filters.assignee}
        onToggle={(id: string) => onChange({ ...filters, assignee: filters.assignee === id ? null : id })}
      />
    </div>
  );
};

interface AssigneeStackProps {
  members: DirectoryMember[];
  selected: string | null;
  onToggle: (userId: string) => void;
}

/** How many faces fit before the row starts reading as a crowd rather than a team. */
const STACK_LIMIT: number = 5;

/**
 * Who is on this board, and a one-click way to see only their work.
 *
 * The Assignee chip menu could already do the filtering, but it answers "filter by whom?" only
 * after you open it and read a list of names. The stack answers "who is here?" without any
 * interaction at all, and turns the most common filter — one specific person — into a single
 * click. Selecting is a TOGGLE: clicking the same face again clears the filter, so the way out
 * is the way in.
 */
const AssigneeStack: React.FC<AssigneeStackProps> = ({ members, selected, onToggle }) => {
  // One member is the board owner looking at their own board — a stack of one says nothing.
  if (members.length < 2) return null;
  const visible: DirectoryMember[] = members.slice(0, STACK_LIMIT);
  const overflow: number = members.length - visible.length;

  return (
    <div className="kuika-board-chips__people" role="group" aria-label="Filter by assignee">
      {visible.map((member: DirectoryMember) => (
        <button
          key={member.id}
          type="button"
          className={classNames("kuika-board-chips__person", {
            "kuika-board-chips__person--selected": selected === member.id
          })}
          onClick={() => onToggle(member.id)}
          title={selected === member.id ? `${member.name} — click to clear` : `Show only ${member.name}`}
          aria-pressed={selected === member.id}
          data-testid={`board-people-${member.id}`}
        >
          <BoardAvatar userId={member.id} className="kuika-board__assignee-avatar" ariaHidden />
        </button>
      ))}
      {overflow > 0 && (
        <span className="kuika-board-chips__person-overflow" title={`${overflow} more members`}>
          +{overflow}
        </span>
      )}
    </div>
  );
};

export interface FilterableIssue {
  assigneeUserId: string | null;
  priority: IssuePriority;
  type: IssueType;
}

export function matchesFilters(issue: FilterableIssue, filters: BoardFilters): boolean {
  if (filters.assignee && issue.assigneeUserId !== filters.assignee) return false;
  if (filters.priority && issue.priority !== filters.priority) return false;
  if (filters.type && issue.type !== filters.type) return false;
  return true;
}

export default FilterChips;
